import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';

const SearchableDropdown = ({ 
  options = [], 
  value = '', 
  onChange, 
  placeholder = "Search or select...", 
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => 
        option.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    inputRef.current?.blur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Display value: show selected value or search term
  const displayValue = isOpen ? searchTerm : value;
  const showClearButton = !disabled && value && !isOpen;

  return (
    <div
      className={`searchable-dropdown ${className} ${disabled ? 'disabled' : ''}`}
      ref={containerRef}
      style={{ outline: 'none', position: 'relative' }}
      tabIndex={-1}
    >
      <div className="dropdown-input-container" style={{ display: 'flex', flex: '1 1 0%', height: '100%', position: 'relative' }}>
        <input
          id="searchable-dropdown-input"
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={value ? value : placeholder}
          disabled={disabled}
          className="paragraph_normal_regular w-full outline-none bg-gray-100 rounded-md border border-solid border-gray-300 px-4 py-3 focus:border-gray-500"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="both"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="searchable-dropdown-listbox"
        />
        
        <div className="dropdown-icons">
          {showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="clear-button"
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="dropdown-toggle"
            disabled={disabled}
            tabIndex={-1}
          >
            <ChevronDown 
              size={16} 
              className={`dropdown-chevron ${isOpen ? 'open' : ''}`}
            />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="dropdown-menu show">
          <ul ref={listRef} className="dropdown-list" id="searchable-dropdown-listbox" role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={`${option}-${index}`}
                  className={`dropdown-item ${
                    index === highlightedIndex ? 'highlighted' : ''
                  } ${value === option ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option}
                </li>
              ))
            ) : (
              <li className="dropdown-no-options">
                No options found for "{searchTerm}"
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;